/** @jsxImportSource @emotion/react */
import c from 'classnames';
import React, { useState } from 'react';
import tw from 'twin.macro';
import { getTimeAgo } from '../utils/dates';
import { Menu } from '@mantine/core';

const Card = tw.div`rounded shadow-lg flex flex-col bg-white`;
export default Card;

interface CardHeaderProps {
    onClick: () => void;
    children: React.ReactChild;
    status?: React.ReactNode;
}
export function CardHeader({ onClick, status, children }: CardHeaderProps) {
    return (
        <div className="relative">
            <div className="w-full">{children}</div>
            <button onClick={onClick}>
                <div className="hover:bg-transparent transition duration-300 absolute bottom-0 top-0 right-0 left-0 bg-gray-900 opacity-25"></div>
            </button>
            {status && <div className="absolute top-0 right-0 px-2 py-2">{status}</div>}
        </div>
    );
}

interface CardDescriptionProps {
    title: React.ReactNode;
    description?: string;
}

export function CardDescription({ title, description }: CardDescriptionProps) {
    return (
        <div className="px-6 py-4 mb-auto">
            {title}
            {description && <p className="text-gray-500 text-sm">{description}</p>}
        </div>
    );
}

export const CardFooter = tw.div`px-6 py-3 flex flex-row items-center bg-white`;

export function CardFooterDate(props: { date?: string }) {
    if (!props.date) {
        return null;
    }
    return (
        <span className="py-1 text-xs font-regular text-gray-900 mr-1 flex flex-row items-center">
            <svg
                height="13px"
                width="13px"
                version="1.1"
                id="Layer_1"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                x="0px"
                y="0px"
                viewBox="0 0 512 512"
                xmlSpace="preserve"
            >
                <g>
                    <g>
                        <path
                            d="M256,0C114.837,0,0,114.837,0,256s114.837,256,256,256s256-114.837,256-256S397.163,0,256,0z M277.333,256
c0,11.797-9.536,21.333-21.333,21.333h-85.333c-11.797,0-21.333-9.536-21.333-21.333s9.536-21.333,21.333-21.333h64v-128
c0-11.797,9.536-21.333,21.333-21.333s21.333,9.536,21.333,21.333V256z"
                        />
                    </g>
                </g>
            </svg>
            <span className="ml-1" title={props.date}>
                {getTimeAgo(new Date(props.date))}
            </span>
        </span>
    );
}

interface CardMenuProps {
    items: { title: string; onClick: (closeMenu: () => void) => void; color?: 'RED' | 'GREY' }[];
}

export function CardMenu({ items }: CardMenuProps) {
    const [opened, setOpened] = useState(false);
    const handleCloseMenu = () => setOpened(false);
    return (
        <Menu withinPortal opened={opened} onChange={setOpened}>
            <span className="py-1 text-xs font-regular text-gray-900 mr-1 flex flex-row items-center ml-auto">
                <Menu.Target>
                    <button type="button" className="relative cursor-pointer">
                        <svg
                            className="h-5 fill-current text-gray-900 hover:text-gray-600"
                            viewBox="0 0 1792 1792"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M1088 1248v192q0 40-28 68t-68 28h-192q-40 0-68-28t-28-68v-192q0-40 28-68t68-28h192q40 0 68 28t28 68zm0-512v192q0 40-28 68t-68 28h-192q-40 0-68-28t-28-68v-192q0-40 28-68t68-28h192q40 0 68 28t28 68zm0-512v192q0 40-28 68t-68 28h-192q-40 0-68-28t-28-68v-192q0-40 28-68t68-28h192q40 0 68 28t28 68z" />
                        </svg>
                    </button>
                </Menu.Target>
                <Menu.Dropdown>
                    {items.map((item) => (
                        <Menu.Item
                            key={item.title}
                            onClick={() => item.onClick(handleCloseMenu)}
                            color={(item?.color ?? 'RED') === 'RED' ? 'red' : undefined}
                        >
                            {item.title}
                        </Menu.Item>
                    ))}
                </Menu.Dropdown>
            </span>
        </Menu>
    );
}
